import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Grid,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  Search as SearchIcon,
  FilterAlt as FilterAltIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as FileDownloadIcon,
  AccountBalance as AccountBalanceIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Payment as PaymentIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import InformesBackButton from '../../components/shared/InformesBackButton';
import ExportProgressDialog, { ExportProgressState } from '../../components/shared/ExportProgressDialog';
import PDFViewer from '../../components/shared/PDFViewer';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import tesoreriaService from '../../services/tesoreriaService';
import { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import ChequeTerceroDetalleDialog from '../../components/Tesoreria/ChequeTerceroDetalleDialog';
import {
  etiquetaEstadoChequeTercero,
  esChequeDisponibleEnCartera,
  resolverEstadoChequeTercero,
} from '../../utils/chequeTerceroEstado';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

interface ChequesTercerosCarteraFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  nroBanco?: string;
  bancoNombre?: string;
  nroSucursal?: string;
  fechaIngresoDesde?: string;
  fechaIngresoHasta?: string;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
  importeDesde?: string;
  importeHasta?: string;
  mcp?: string;
  estado?: string;
  codigoCajaFiltro?: number | '';
  nombreCajaFiltro?: string;
}

interface ChequeCartera {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number | null;
  nroDestino: number | null;
  fechaSalida: string | null;
  nroMovCajaSalida: number | null;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
  senial?: number;
}

const extractChequesCarteraFromResponse = (data: unknown): ChequeCartera[] => {
  const payload = data as { chequesCartera?: ChequeCartera[]; items?: ChequeCartera[] };
  if (payload?.chequesCartera && Array.isArray(payload.chequesCartera)) return payload.chequesCartera;
  if (payload?.items && Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(data)) return data;
  return [];
};

const resolveChequesCarteraTotalCount = (data: unknown): number | null => {
  const payload = data as { totalCount?: number; TotalCount?: number };
  const raw = payload?.totalCount ?? payload?.TotalCount;
  if (raw == null || !Number.isFinite(raw) || raw < 0) return null;
  return raw;
};

interface Banco {
  id: number;
  nombre: string;
  nroBanco?: string | number;
}

interface SucursalBanco {
  nroBanco: number;
  nroSucursal: number;
  sucursal: string;
}

interface McpOption {
  mcp: string;
  descripcion?: string;
}

const parseCodigoCajaParam = (value: string | null): number | '' => {
  if (!value?.trim()) return '';
  const n = parseInt(value.trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : '';
};

/** Evita que Excel convierta identificadores largos a notación científica. */
const excelTexto = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const leerNroCheque = (cheque: ChequeCartera): string => {
  const raw = cheque as ChequeCartera & { NroCheque?: unknown };
  return excelTexto(raw.nroCheque ?? raw.NroCheque);
};

const aplicarColumnasTextoExcel = (ws: XLSX.WorkSheet, columnasTexto: string[]) => {
  if (!ws['!ref']) return;

  const range = XLSX.utils.decode_range(ws['!ref']);
  const headerRow = range.s.r;
  const columnas = new Set(columnasTexto);

  for (let c = range.s.c; c <= range.e.c; c++) {
    const headerCell = ws[XLSX.utils.encode_cell({ r: headerRow, c })];
    const header = headerCell?.v;
    if (typeof header !== 'string' || !columnas.has(header)) continue;

    for (let r = headerRow + 1; r <= range.e.r; r++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = ws[ref];
      if (!cell || cell.v === null || cell.v === undefined || cell.v === '') continue;
      cell.t = 's';
      cell.v = String(cell.v);
      cell.z = '@';
      delete cell.w;
    }
  }
};

const ChequesTercerosCartera: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const loadStored = () =>
    loadTesoreriaFiltros<ChequesTercerosCarteraFiltrosStored>(TESORERIA_FILTROS_KEYS.chequesTercerosCartera);

  const [cheques, setCheques] = useState<ChequeCartera[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 20));
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const { enqueueSnackbar } = useSnackbar();

  // Filtros
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [sucursales, setSucursales] = useState<SucursalBanco[]>([]);
  const [nroBanco, setNroBanco] = useState(() => storedString(loadStored(), 'nroBanco', ''));
  const [bancoNombre, setBancoNombre] = useState(() => storedString(loadStored(), 'bancoNombre', ''));
  const [nroSucursal, setNroSucursal] = useState(() => storedString(loadStored(), 'nroSucursal', ''));
  const [fechaIngresoDesde, setFechaIngresoDesde] = useState(() =>
    storedString(loadStored(), 'fechaIngresoDesde', '')
  );
  const [fechaIngresoHasta, setFechaIngresoHasta] = useState(() =>
    storedString(loadStored(), 'fechaIngresoHasta', '')
  );
  const [fechaVencimientoDesde, setFechaVencimientoDesde] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoDesde', '')
  );
  const [fechaVencimientoHasta, setFechaVencimientoHasta] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoHasta', '')
  );
  const [importeDesde, setImporteDesde] = useState(() => storedString(loadStored(), 'importeDesde', ''));
  const [importeHasta, setImporteHasta] = useState(() => storedString(loadStored(), 'importeHasta', ''));
  const [mcp, setMcp] = useState(() => storedString(loadStored(), 'mcp', ''));
  const [listaMcps, setListaMcps] = useState<McpOption[]>([]);
  const [estado, setEstado] = useState(() => {
    const stored = loadStored().estado;
    // Por defecto solo disponibles; vacío o ausente = en cartera.
    if (stored == null || String(stored).trim() === '') return 'en_cartera';
    return String(stored).trim();
  });
  const [codigoCajaFiltro, setCodigoCajaFiltro] = useState<number | ''>(() => {
    const fromUrl = parseCodigoCajaParam(searchParams.get('codigoCaja'));
    if (fromUrl !== '') return fromUrl;
    const stored = loadStored().codigoCajaFiltro;
    if (typeof stored === 'number' && stored > 0) return stored;
    return '';
  });
  const [nombreCajaFiltro, setNombreCajaFiltro] = useState<string>(() => {
    const state = location.state as { nombreCaja?: string } | null;
    if (state?.nombreCaja?.trim()) return state.nombreCaja.trim();
    return storedString(loadStored(), 'nombreCajaFiltro', '');
  });

  // Modal de banco
  const [openBancoDialog, setOpenBancoDialog] = useState(false);
  const [bancoSearch, setBancoSearch] = useState('');
  const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
  const [bancoPage, setBancoPage] = useState(1);
  const [bancoLoading, setBancoLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgressState>({
    message: '',
    currentPage: 0,
    totalPages: 0,
  });
  const resetExportProgress = () =>
    setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const [chequeSeleccionado, setChequeSeleccionado] = useState<ChequeCartera | null>(null);
  const [detalleReloadToken, setDetalleReloadToken] = useState(0);
  /** Selección múltiple por nroCarga (persiste entre páginas). */
  const [chequesSeleccionados, setChequesSeleccionados] = useState<Map<number, ChequeCartera>>(new Map());

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.chequesTercerosCartera,
    {
      searchTerm,
      rowsPerPage,
      nroBanco,
      bancoNombre,
      nroSucursal,
      fechaIngresoDesde,
      fechaIngresoHasta,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      importeDesde,
      importeHasta,
      mcp,
      estado,
      codigoCajaFiltro,
      nombreCajaFiltro,
    },
    [
      searchTerm,
      rowsPerPage,
      nroBanco,
      bancoNombre,
      nroSucursal,
      fechaIngresoDesde,
      fechaIngresoHasta,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      importeDesde,
      importeHasta,
      mcp,
      estado,
      codigoCajaFiltro,
      nombreCajaFiltro,
    ]
  );

  useEffect(() => {
    const codigo = parseCodigoCajaParam(searchParams.get('codigoCaja'));
    setCodigoCajaFiltro(codigo);
    if (codigo === '') {
      setNombreCajaFiltro('');
    } else {
      const state = location.state as { nombreCaja?: string } | null;
      if (state?.nombreCaja?.trim()) {
        setNombreCajaFiltro(state.nombreCaja.trim());
      }
    }
  }, [searchParams, location.state]);

  // Cargar sucursales
  useEffect(() => {
    const loadSucursales = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const sucursalesResponse = await api.get('/api/Tesoreria/sucursales-banco', { headers });
        setSucursales(sucursalesResponse.data || []);
      } catch (error: any) {
        console.error('Error al cargar sucursales:', error);
      }
    };
    loadSucursales();
  }, []);

  // Cargar MCPs para el filtro
  useEffect(() => {
    const loadMcps = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const response = await api.get('/api/Tesoreria/cheques-cartera/mcps', { headers });
        const data = response.data || [];
        setListaMcps(Array.isArray(data) ? data.map((it: any) => ({ mcp: it.mcp ?? it.Mcp ?? '', descripcion: it.descripcion ?? it.Descripcion })) : []);
      } catch (error: any) {
        console.error('Error al cargar MCPs:', error);
        setListaMcps([]);
      }
    };
    loadMcps();
  }, []);

  // Cargar bancos para el modal
  useEffect(() => {
    const loadBancos = async (reset = false) => {
      try {
        setBancoLoading(true);
        const pageToLoad = reset ? 1 : bancoPage;
        const items = await tesoreriaService.getBancosPaged({
          searchTerm: bancoSearch || undefined,
          pageNumber: pageToLoad,
          pageSize: 20
        });
        setBancos(prev => reset ? items : [...prev, ...items]);
        if (reset) setBancoPage(1);
      } catch (error) {
        console.error('Error al cargar bancos:', error);
      } finally {
        setBancoLoading(false);
      }
    };

    if (openBancoDialog) {
      const timer = setTimeout(() => loadBancos(true), bancoSearch ? 500 : 0);
      return () => clearTimeout(timer);
    }
  }, [openBancoDialog, bancoSearch]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params: any = {
          pageNumber: page + 1,
          pageSize: Math.min(rowsPerPage, 20)
        };

        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }

        if (nroBanco) {
          params.nroBanco = parseInt(nroBanco);
        }

        if (nroSucursal) {
          params.nroSucursal = parseInt(nroSucursal);
        }

        if (fechaIngresoDesde) {
          params.fechaDesde = fechaIngresoDesde;
        }

        if (fechaIngresoHasta) {
          params.fechaHasta = fechaIngresoHasta;
        }

        if (fechaVencimientoDesde) {
          params.fechaVencimientoDesde = fechaVencimientoDesde;
        }

        if (fechaVencimientoHasta) {
          params.fechaVencimientoHasta = fechaVencimientoHasta;
        }

        if (importeDesde && !isNaN(parseFloat(importeDesde))) {
          params.importeDesde = parseFloat(importeDesde);
        }

        if (importeHasta && !isNaN(parseFloat(importeHasta))) {
          params.importeHasta = parseFloat(importeHasta);
        }

        if (mcp.trim()) {
          params.mcp = mcp.trim();
        }

        if (estado.trim()) {
          params.estado = estado.trim();
        }

        if (codigoCajaFiltro !== '') {
          params.CodigoCaja = codigoCajaFiltro;
        }

        const response = await api.get('/api/Tesoreria/cheques-cartera', {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          params
        });

        const data = response.data;
        const chequesArray = extractChequesCarteraFromResponse(data);
        const count = resolveChequesCarteraTotalCount(data) ?? chequesArray.length;
        setCheques(chequesArray);
        setTotalCount(count);
      } catch (error: any) {
        console.error('Error al obtener los cheques de terceros en cartera:', error);
        enqueueSnackbar(
          error.response?.data?.message || 'No se pudieron cargar los cheques de terceros en cartera.',
          { variant: 'error' }
        );
        setCheques([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0);

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage, nroBanco, nroSucursal, fechaIngresoDesde, fechaIngresoHasta, fechaVencimientoDesde, fechaVencimientoHasta, importeDesde, importeHasta, mcp, estado, codigoCajaFiltro, enqueueSnackbar, detalleReloadToken]);

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(parseInt(event.target.value, 10), 20);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleBancoSelect = () => {
    const banco = bancos.find(b => b.id === bancoSeleccionadoId);
    if (banco && banco.nroBanco) {
      setNroBanco(banco.nroBanco.toString());
      setBancoNombre(`${banco.nombre} (${banco.nroBanco})`);
      setNroSucursal('');
      setPage(0);
    }
    setOpenBancoDialog(false);
    setBancoSearch('');
  };

  const handleSucursalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNroSucursal(event.target.value);
    setPage(0);
  };

  const handleClearCajaFiltro = () => {
    setCodigoCajaFiltro('');
    setNombreCajaFiltro('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('codigoCaja');
        return next;
      },
      { replace: true }
    );
    setPage(0);
  };

  const handleClearFilters = () => {
    setNroBanco('');
    setBancoNombre('');
    setNroSucursal('');
    setFechaIngresoDesde('');
    setFechaIngresoHasta('');
    setFechaVencimientoDesde('');
    setFechaVencimientoHasta('');
    setImporteDesde('');
    setImporteHasta('');
    setMcp('');
    setEstado('en_cartera');
    setSearchTerm('');
    handleClearCajaFiltro();
    setChequesSeleccionados(new Map());
    setPage(0);
  };

  const handleBancoScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight;
    if (bottom && !bancoLoading) {
      setBancoPage(prev => prev + 1);
    }
  };

  const sucursalesFiltradas = nroBanco
    ? sucursales.filter(s => s.nroBanco === parseInt(nroBanco))
    : [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getEstadoChip = (cheque: ChequeCartera) => {
    const estado = resolverEstadoChequeTercero(cheque);
    const label = etiquetaEstadoChequeTercero(estado);
    const color =
      estado === 'anulado'
        ? 'default'
        : estado === 'conciliado'
          ? 'success'
          : estado === 'entregado'
            ? 'warning'
            : 'info';
    return <Chip label={label} color={color} size="small" />;
  };

  const getEstadoTexto = (cheque: ChequeCartera) =>
    etiquetaEstadoChequeTercero(resolverEstadoChequeTercero(cheque));

  const cellWithHover = (title: React.ReactNode, content: React.ReactNode) => (
    <Tooltip
      title={
        typeof title === 'string' ? (
          <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
            {title || '-'}
          </Box>
        ) : (
          title || '-'
        )
      }
      arrow
      enterDelay={400}
    >
      <Box component="span" sx={{ display: 'block', maxWidth: '100%', overflow: 'hidden' }}>
        {content}
      </Box>
    </Tooltip>
  );

  const chequesSeleccionadosLista = useMemo(
    () => Array.from(chequesSeleccionados.values()),
    [chequesSeleccionados]
  );

  const chequesDisponiblesSeleccionados = useMemo(
    () => chequesSeleccionadosLista.filter(esChequeDisponibleEnCartera),
    [chequesSeleccionadosLista]
  );

  const cantidadSeleccionados = chequesDisponiblesSeleccionados.length;

  const importeSeleccionado = useMemo(
    () => chequesDisponiblesSeleccionados.reduce((sum, c) => sum + (c.importe || 0), 0),
    [chequesDisponiblesSeleccionados]
  );

  const todosPaginaSeleccionados = (() => {
    const disponibles = cheques.filter(esChequeDisponibleEnCartera);
    return disponibles.length > 0 && disponibles.every((c) => chequesSeleccionados.has(c.nroCarga));
  })();

  const algunoPaginaSeleccionado =
    cheques.some((c) => esChequeDisponibleEnCartera(c) && chequesSeleccionados.has(c.nroCarga));

  const handleToggleCheque = (cheque: ChequeCartera, checked: boolean) => {
    setChequesSeleccionados((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(cheque.nroCarga, cheque);
      } else {
        next.delete(cheque.nroCarga);
      }
      return next;
    });
  };

  const handleToggleTodosPagina = () => {
    setChequesSeleccionados((prev) => {
      const next = new Map(prev);
      const disponibles = cheques.filter(esChequeDisponibleEnCartera);
      const todosDisponiblesSeleccionados =
        disponibles.length > 0 && disponibles.every((c) => prev.has(c.nroCarga));
      if (todosDisponiblesSeleccionados) {
        disponibles.forEach((c) => next.delete(c.nroCarga));
      } else {
        disponibles.forEach((c) => next.set(c.nroCarga, c));
      }
      return next;
    });
  };

  const handleIrOrdenPago = () => {
    if (chequesDisponiblesSeleccionados.length === 0) {
      enqueueSnackbar('Ninguno de los cheques seleccionados está disponible en cartera.', {
        variant: 'warning',
      });
      return;
    }
    if (chequesDisponiblesSeleccionados.length < chequesSeleccionadosLista.length) {
      enqueueSnackbar(
        `Se omitieron ${chequesSeleccionadosLista.length - chequesDisponiblesSeleccionados.length} cheque(s) que ya no están disponibles en cartera.`,
        { variant: 'info' }
      );
    }
    const nrosCarga = chequesDisponiblesSeleccionados.map((c) => c.nroCarga);
    navigate('/tesoreria/orden-pago/nuevo', {
      state: {
        nrosCargaPreseleccionados: nrosCarga,
        chequesPreseleccionados: chequesDisponiblesSeleccionados,
      },
    });
  };

  const handleIrTraspasos = () => {
    if (chequesDisponiblesSeleccionados.length === 0) {
      enqueueSnackbar('Ninguno de los cheques seleccionados está disponible en cartera.', {
        variant: 'warning',
      });
      return;
    }
    if (chequesDisponiblesSeleccionados.length < chequesSeleccionadosLista.length) {
      enqueueSnackbar(
        `Se omitieron ${chequesSeleccionadosLista.length - chequesDisponiblesSeleccionados.length} cheque(s) que ya no están disponibles en cartera.`,
        { variant: 'info' }
      );
    }
    const mcps = new Set(
      chequesDisponiblesSeleccionados.map((c) => (c.mcp ?? '').trim()).filter(Boolean)
    );
    if (mcps.size > 1) {
      enqueueSnackbar(
        'Los cheques seleccionados deben pertenecer al mismo MCP para traspasar juntos.',
        { variant: 'warning' }
      );
      return;
    }
    const mcpOrigen = chequesDisponiblesSeleccionados[0]?.mcp?.trim() ?? '';
    if (!mcpOrigen) {
      enqueueSnackbar('Los cheques seleccionados no tienen MCP asociado.', { variant: 'warning' });
      return;
    }
    const nrosCarga = chequesDisponiblesSeleccionados.map((c) => c.nroCarga);
    navigate('/tesoreria/transferencias', {
      state: {
        abrirNuevoTraspaso: true,
        nrosCargaPreseleccionados: nrosCarga,
        chequesPreseleccionados: chequesDisponiblesSeleccionados,
        mcpOrigenPreseleccionado: mcpOrigen,
        codigoCaja: codigoCajaFiltro !== '' ? codigoCajaFiltro : undefined,
      },
    });
  };

  const buildExportParams = (pageNumber: number, pageSize: number) => {
    const params: any = { pageNumber, pageSize };
    if (searchTerm.trim()) params.searchTerm = searchTerm.trim();
    if (nroBanco) params.nroBanco = parseInt(nroBanco);
    if (nroSucursal) params.nroSucursal = parseInt(nroSucursal);
    if (fechaIngresoDesde) params.fechaDesde = fechaIngresoDesde;
    if (fechaIngresoHasta) params.fechaHasta = fechaIngresoHasta;
    if (fechaVencimientoDesde) params.fechaVencimientoDesde = fechaVencimientoDesde;
    if (fechaVencimientoHasta) params.fechaVencimientoHasta = fechaVencimientoHasta;
    if (importeDesde && !isNaN(parseFloat(importeDesde))) params.importeDesde = parseFloat(importeDesde);
    if (importeHasta && !isNaN(parseFloat(importeHasta))) params.importeHasta = parseFloat(importeHasta);
    if (mcp.trim()) params.mcp = mcp.trim();
    if (estado.trim()) params.estado = estado.trim();
    if (codigoCajaFiltro !== '') params.CodigoCaja = codigoCajaFiltro;
    return params;
  };

  const fetchExportItems = async (
    onProgress?: (currentPage: number, totalPages: number) => void
  ): Promise<{ items: ChequeCartera[]; apiTotalCount: number; rawFetchedCount: number }> => {
    const token = localStorage.getItem('token');
    const pageSize = 20;
    const allItems: any[] = [];
    let pageNumber = 1;
    let apiTotalCount = 0;

    while (pageNumber <= 500) {
      const response = await api.get('/api/Tesoreria/cheques-cartera', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: buildExportParams(pageNumber, pageSize),
      });
      const data = response.data;
      const items = extractChequesCarteraFromResponse(data);

      const reportedTotal = resolveChequesCarteraTotalCount(data);
      if (reportedTotal != null) {
        apiTotalCount = reportedTotal;
      }

      const totalPages =
        apiTotalCount > 0 ? Math.max(1, Math.ceil(apiTotalCount / pageSize)) : 0;

      if (!items.length) {
        onProgress?.(Math.max(pageNumber - 1, 1), pageNumber > 1 ? pageNumber - 1 : 1);
        break;
      }

      allItems.push(...items);

      const fetchedAll = apiTotalCount > 0 && allItems.length >= apiTotalCount;
      const isLastPage = items.length < pageSize;

      if (fetchedAll || isLastPage) {
        onProgress?.(pageNumber, pageNumber);
        break;
      }

      onProgress?.(pageNumber, totalPages);
      pageNumber++;
    }

    return { items: allItems, apiTotalCount, rawFetchedCount: allItems.length };
  };

  const mapChequeToExportRow = (c: ChequeCartera) => ({
    'Nro. Carga': excelTexto(c.nroCarga),
    'Nro. Cheque': leerNroCheque(c),
    'Banco': c.nombreBanco?.trim() || '',
    'Sucursal': c.nombreSucursal?.trim() || '',
    'Librador': c.librador?.trim() || c.nroLibrador?.trim() || '',
    'Importe': c.importe || 0,
    'Fecha Emisión': formatDate(c.fechaEmision),
    'Fecha Vencimiento': formatDate(c.fechaVencimiento),
    'Fecha Ingreso': formatDate(c.fechaIngreso),
    'Fecha Salida': formatDate(c.fechaSalida || ''),
    'Comprobante Ingreso': c.comprobanteIngreso?.trim() || '',
    'Comprobante Salida': c.comprobanteSalida?.trim() || '',
    'Concepto': c.concepto?.trim() || '',
    'MCP': c.mcp?.trim() || '',
    'Estado': getEstadoTexto(c)
  });

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });

      const { items, apiTotalCount, rawFetchedCount } = await fetchExportItems((current, total) => {
        setExportProgress({
          message:
            total > 0
              ? `Obteniendo datos (${current} de ${total} páginas)...`
              : 'Obteniendo datos...',
          currentPage: current,
          totalPages: total,
        });
      });

      if (items.length === 0) {
        enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
        return;
      }
      if (apiTotalCount > 0 && rawFetchedCount < apiTotalCount) {
        enqueueSnackbar(
          `Se exportaron ${items.length} de ${apiTotalCount} registros del servidor. Revise los filtros o intente nuevamente.`,
          { variant: 'warning' }
        );
      }

      setExportProgress({ message: 'Generando archivo Excel...', currentPage: 0, totalPages: 0 });

      const datos = items.map(mapChequeToExportRow);
      const ws = XLSX.utils.json_to_sheet(datos);
      aplicarColumnasTextoExcel(ws, ['Nro. Carga', 'Nro. Cheque']);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cheques en Cartera');
      const fileName = `Cheques_Terceros_Cartera_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar(`Exportado correctamente (${items.length} registros)`, { variant: 'success' });
    } catch (error: any) {
      console.error('Error al exportar:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error al exportar a Excel', { variant: 'error' });
    } finally {
      setExportingExcel(false);
      resetExportProgress();
    }
  };

  const handleExportPdf = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });

      const { items, apiTotalCount, rawFetchedCount } = await fetchExportItems((current, total) => {
        setExportProgress({
          message:
            total > 0
              ? `Obteniendo datos (${current} de ${total} páginas)...`
              : 'Obteniendo datos...',
          currentPage: current,
          totalPages: total,
        });
      });

      if (items.length === 0) {
        enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
        return;
      }
      if (apiTotalCount > 0 && rawFetchedCount < apiTotalCount) {
        enqueueSnackbar(
          `Se exportaron ${items.length} de ${apiTotalCount} registros del servidor. Revise los filtros o intente nuevamente.`,
          { variant: 'warning' }
        );
      }

      setExportProgress({ message: 'Generando archivo PDF...', currentPage: 0, totalPages: 0 });

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 36;
      const marginY = 44;
      let currentY = marginY;

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Cheques de Terceros en Cartera', marginX, currentY);
      currentY += 22;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 12;
      doc.text(`Registros: ${items.length}`, marginX, currentY);
      currentY += 12;

      if (codigoCajaFiltro !== '') {
        doc.text(
          `Caja: ${codigoCajaFiltro}${nombreCajaFiltro ? ` — ${nombreCajaFiltro}` : ''}`,
          marginX,
          currentY
        );
        currentY += 12;
      }

      if (searchTerm.trim()) {
        doc.text(`Búsqueda: ${searchTerm.trim()}`, marginX, currentY);
        currentY += 12;
      }

      currentY += 6;

      const tableData = items.map((c) => [
        excelTexto(c.nroCarga),
        leerNroCheque(c),
        c.nombreBanco?.trim() || '',
        c.nombreSucursal?.trim() || '',
        c.librador?.trim() || c.nroLibrador?.trim() || '',
        formatCurrency(c.importe || 0),
        formatDate(c.fechaEmision),
        formatDate(c.fechaVencimiento),
        formatDate(c.fechaIngreso),
        formatDate(c.fechaSalida || ''),
        c.comprobanteIngreso?.trim() || '',
        c.comprobanteSalida?.trim() || '',
        c.concepto?.trim() || '',
        c.mcp?.trim() || '',
        getEstadoTexto(c)
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [[
          'Nro. Carga',
          'Nro. Cheque',
          'Banco',
          'Sucursal',
          'Librador',
          'Importe',
          'F. Emisión',
          'F. Venc.',
          'F. Ingreso',
          'F. Salida',
          'Comp. Ing.',
          'Comp. Sal.',
          'Concepto',
          'MCP',
          'Estado'
        ]],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 5.5, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 6 },
        columnStyles: {
          5: { halign: 'right' }
        }
      });

      const pdfBlob = doc.output('blob');
      const safeBlob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al generar PDF:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error al generar el PDF', { variant: 'error' });
    } finally {
      setExportingPdf(false);
      resetExportProgress();
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <InformesBackButton to="/tesoreria/informes" useBrowserBack />
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Cheques de Terceros en Cartera
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Tooltip
                title={
                  cantidadSeleccionados === 0
                    ? 'Seleccioná al menos un cheque'
                    : 'Traspasar los cheques seleccionados a otra caja'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SwapHorizIcon />}
                    onClick={handleIrTraspasos}
                    disabled={cantidadSeleccionados === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    Traspasos
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  cantidadSeleccionados === 0
                    ? 'Seleccioná al menos un cheque'
                    : 'Crear orden de pago endosando los cheques seleccionados'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PaymentIcon />}
                    onClick={handleIrOrdenPago}
                    disabled={cantidadSeleccionados === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    Orden de pago
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcel()}
                disabled={loading || exportingExcel || exportingPdf || totalCount === 0}
                sx={{ textTransform: 'none' }}
              >
                {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handleExportPdf()}
                disabled={loading || exportingExcel || exportingPdf || totalCount === 0}
                sx={{ textTransform: 'none' }}
              >
                {exportingPdf ? 'Generando PDF…' : 'Imprimir PDF'}
              </Button>
            </Stack>
          </Stack>

          {codigoCajaFiltro !== '' && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={`Caja: ${codigoCajaFiltro}${nombreCajaFiltro ? ` — ${nombreCajaFiltro}` : ''}`}
                onDelete={handleClearCajaFiltro}
                color="primary"
                variant="outlined"
              />
            </Stack>
          )}

          {cantidadSeleccionados > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ mb: 2 }}
            >
              <Chip
                color="primary"
                label={`${cantidadSeleccionados} seleccionado${cantidadSeleccionados > 1 ? 's' : ''} · ${formatCurrency(importeSeleccionado)}`}
              />
              <Button
                size="small"
                onClick={() => setChequesSeleccionados(new Map())}
                sx={{ textTransform: 'none' }}
              >
                Limpiar selección
              </Button>
            </Stack>
          )}

          {/* Búsqueda */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por librador, banco, número de cheque..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 500 }}
            />
          </Box>

          {/* Filtros Avanzados */}
          <Accordion sx={{ mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: '#f9f9f9' }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterAltIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ color: '#1976d2' }}>
                  Filtros Avanzados
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Banco"
                    value={bancoNombre}
                    placeholder="Seleccionar banco..."
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setOpenBancoDialog(true)}
                            size="small"
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    onClick={() => setOpenBancoDialog(true)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Sucursal"
                    value={nroSucursal}
                    onChange={handleSucursalChange}
                    disabled={!nroBanco}
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {sucursalesFiltradas.map((sucursal) => (
                      <MenuItem key={sucursal.nroSucursal} value={sucursal.nroSucursal.toString()}>
                        {sucursal.sucursal}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Ingreso Desde"
                    value={fechaIngresoDesde}
                    onChange={(e) => {
                      setFechaIngresoDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Ingreso Hasta"
                    value={fechaIngresoHasta}
                    onChange={(e) => {
                      setFechaIngresoHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Desde"
                    value={fechaVencimientoDesde}
                    onChange={(e) => {
                      setFechaVencimientoDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Hasta"
                    value={fechaVencimientoHasta}
                    onChange={(e) => {
                      setFechaVencimientoHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Monto Desde"
                    value={importeDesde}
                    onChange={(e) => {
                      setImporteDesde(e.target.value);
                      setPage(0);
                    }}
                    placeholder="0"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Monto Hasta"
                    value={importeHasta}
                    onChange={(e) => {
                      setImporteHasta(e.target.value);
                      setPage(0);
                    }}
                    placeholder="0"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Estado"
                    value={estado}
                    onChange={(e) => {
                      setEstado(e.target.value);
                      setPage(0);
                    }}
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="en_cartera">En cartera</MenuItem>
                    <MenuItem value="entregado">Entregado</MenuItem>
                    <MenuItem value="conciliado">Conciliado</MenuItem>
                    <MenuItem value="anulado">Anulado</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    {...searchableSelectAutocompleteProps}
                    options={[{ mcp: '', descripcion: 'Todos' }, ...listaMcps]}
                    getOptionLabel={(option) =>
                      option.mcp ? (option.descripcion ? `${option.mcp} - ${option.descripcion}` : option.mcp) : 'Todos'
                    }
                    value={mcp ? (listaMcps.find((o) => o.mcp === mcp) ?? { mcp, descripcion: mcp }) : { mcp: '', descripcion: 'Todos' }}
                    onChange={(_, newValue) => {
                      setMcp(newValue?.mcp ?? '');
                      setPage(0);
                    }}
                    isOptionEqualToValue={(opt, val) => opt.mcp === val.mcp}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue?.trim()) return options;
                      const term = inputValue.trim().toLowerCase();
                      return options.filter(
                        (opt) =>
                          (opt.mcp ?? '').toLowerCase().includes(term) ||
                          (opt.descripcion ?? '').toLowerCase().includes(term)
                      );
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: 56,
                        fontSize: '1rem',
                        padding: '8px 14px',
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="MCP"
                        placeholder="Buscar..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#666', fontSize: 22 }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.mcp || 'todos'} sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <AccountBalanceIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              {option.descripcion || option.mcp || 'Todos'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.mcp ? `${option.mcp}` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    size="small"
                  >
                    Limpiar Filtros
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Tabla */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableCell-root': {
                      px: 1,
                      py: 0.75,
                    },
                    '& .MuiTableCell-paddingCheckbox': {
                      px: 0.5,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell padding="checkbox" sx={{ width: 40 }}>
                        <Checkbox
                          indeterminate={algunoPaginaSeleccionado && !todosPaginaSeleccionados}
                          checked={todosPaginaSeleccionados}
                          onChange={handleToggleTodosPagina}
                          disabled={cheques.length === 0}
                          inputProps={{ 'aria-label': 'seleccionar todos los cheques de la página' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cheque</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Librador</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Importe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Emisión / Venc.</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Ingreso / Salida</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Comprobantes</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cheques.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {searchTerm ? 'No se encontraron cheques con los criterios de búsqueda' : 'No hay cheques de terceros en cartera'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cheques.map((cheque) => {
                        const seleccionado = chequesSeleccionados.has(cheque.nroCarga);
                        const disponible = esChequeDisponibleEnCartera(cheque);
                        const nroChequeTxt = String(cheque.nroCheque || '-');
                        const bancoTxt = cheque.nombreBanco?.trim() || '-';
                        const sucursalTxt = cheque.nombreSucursal?.trim() || '-';
                        const libradorTxt = cheque.librador?.trim() || cheque.nroLibrador?.trim() || '-';
                        const importeTxt = formatCurrency(cheque.importe || 0);
                        const emisionTxt = formatDate(cheque.fechaEmision);
                        const vencTxt = formatDate(cheque.fechaVencimiento);
                        const ingresoTxt = formatDate(cheque.fechaIngreso);
                        const salidaTxt = formatDate(cheque.fechaSalida || '');
                        const compIngresoTxt = cheque.comprobanteIngreso?.trim() || '-';
                        const compSalidaTxt = cheque.comprobanteSalida?.trim() || '-';
                        const conceptoTxt = cheque.concepto?.trim() || '-';
                        const mcpTxt = cheque.mcp?.trim() || '-';
                        const estadoTxt = getEstadoTexto(cheque);
                        return (
                          <TableRow
                            key={cheque.nroCarga}
                            hover
                            selected={seleccionado}
                            sx={{
                              cursor: 'pointer',
                              opacity: disponible ? 1 : 0.55,
                            }}
                            onClick={() => setChequeSeleccionado(cheque)}
                          >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={seleccionado}
                                disabled={!disponible}
                                onChange={(e) => handleToggleCheque(cheque, e.target.checked)}
                                inputProps={{ 'aria-label': `seleccionar cheque ${cheque.nroCarga}` }}
                              />
                            </TableCell>
                            <TableCell>
                              {cellWithHover(
                                `Cheque: ${nroChequeTxt}\nBanco: ${bancoTxt}\nSucursal: ${sucursalTxt}`,
                                <Stack spacing={0.25}>
                                  <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                                    {nroChequeTxt}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                                    {bancoTxt}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                                    {sucursalTxt}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(libradorTxt, libradorTxt)}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(importeTxt, importeTxt)}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(
                                `Emisión: ${emisionTxt}\nVencimiento: ${vencTxt}`,
                                <Stack spacing={0.25}>
                                  <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                                    {emisionTxt}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }} lineHeight={1.2}>
                                    Vence: {vencTxt}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(
                                `Ingreso: ${ingresoTxt}\nSalida: ${salidaTxt}`,
                                <Stack spacing={0.25}>
                                  <Typography variant="body2" lineHeight={1.2}>
                                    {ingresoTxt}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                                    Salida: {salidaTxt}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(
                                `Ingreso: ${compIngresoTxt}\nSalida: ${compSalidaTxt}`,
                                <Stack spacing={0.25}>
                                  <Typography variant="body2" lineHeight={1.2} noWrap>
                                    {compIngresoTxt}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" lineHeight={1.2} noWrap>
                                    Salida: {compSalidaTxt}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(conceptoTxt, conceptoTxt)}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(mcpTxt, mcpTxt)}
                            </TableCell>
                            <TableCell>
                              {cellWithHover(estadoTxt, getEstadoChip(cheque))}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`}
                />
              </>
            )}
          </TableContainer>
        </CardContent>
      </Card>

      <ChequeTerceroDetalleDialog
        open={!!chequeSeleccionado}
        onClose={() => setChequeSeleccionado(null)}
        cheque={chequeSeleccionado}
        onSaved={() => setDetalleReloadToken((t) => t + 1)}
      />

      {/* Modal de selección de Banco */}
      <Dialog
        open={openBancoDialog}
        onClose={() => setOpenBancoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Seleccionar Banco</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Buscar banco..."
              value={bancoSearch}
              onChange={(e) => setBancoSearch(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }} onScroll={handleBancoScroll}>
            <Grid container spacing={1}>
              {bancos.map((banco, idx) => (
                <Grid item xs={12} key={banco.id ?? idx}>
                  <Card
                    variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setBancoSeleccionadoId(banco.id)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                      <input
                        type="radio"
                        checked={bancoSeleccionadoId === banco.id}
                        onChange={() => setBancoSeleccionadoId(banco.id)}
                        style={{ marginRight: 8 }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>
                        {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {!bancos.length && !bancoLoading && (
                <Grid item xs={12}>
                  <Typography variant="body2">No se encontraron bancos.</Typography>
                </Grid>
              )}
              {bancoLoading && (
                <Grid item xs={12}>
                  <Typography variant="body2">Cargando bancos...</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBancoDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={bancoSeleccionadoId == null}
            onClick={handleBancoSelect}
          >
            Seleccionar
          </Button>
        </DialogActions>
      </Dialog>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Cheques de Terceros en Cartera"
          documentId="cheques-terceros-cartera"
          documentType="informeCliente"
          downloadFileName={`Cheques_Terceros_Cartera_${new Date().toISOString().split('T')[0]}.pdf`}
        />
      ) : null}

      <ExportProgressDialog
        open={exportingExcel || exportingPdf}
        progress={exportProgress}
      />
    </Box>
  );
};

export default ChequesTercerosCartera;

